import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  OnDestroy,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { DateTime } from "luxon";
import { ChartPoint } from "src/app/services/utility.service";
import { DashboardDataService } from "src/app/services/dashboard-data.service";
import { interval, Subscription, forkJoin, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LocalStorageService } from "src/app/services/local-storage.service";
import { BidType, CombinedStats } from "src/app/models/api-response.models";
import { FormatHelperService } from "src/app/services/format-helper.service";

@Component({
  selector: "app-utility-dashboard",
  templateUrl: "./utility-dashboard.component.html",
  styleUrls: ["./utility-dashboard.component.scss"],
})
export class UtilityDashboardComponent implements OnInit, OnDestroy {
  today = "";
  selectedDate = "";
  currentDate = new Date();
  displayDate = "";
  loading = false;
  error = "";
  loadingMessage = "Loading summary…";
  routeData: any = {};
  bid_type: BidType = BidType.Preferential;
  maxDate: string = "";


  showDashesForToday = false;

  stats: {
    buyers?: number;
    sellers?: number;
    active_participants?: number;
    bids_accepted?: number;
    bids_rejected?: number;
    total_energy_traded?: number;
    total_bids?: number;
    strike_price?: number;
    energy_traded?: number;
    untraded_energy?: number;
    total_value?: number;
    percentage_change?: number;
    // New fields from dashboard API
    average_price?: number;
    total_cbdc_transactions?: number;
    total_traded_value?: number;
    total_number_of_consumers?: number;
    total_number_of_prosumers?: number;
    total_number_of_users?: number;
  } | null = null;

  //utilityId = "94522a0a-c8f1-40f8-a2e5-9aed2dc11111";
  utilityId: string | null = null;

  chartGroups: Array<{
    date: string;
    buy_volume: number;
    sell_volume: number;
    total_volume: number;
  }> = [];
  chartLoading = false;
  chartMaxForScale = 1;
  chartTicks: number[] = [];

  readonly SVG = {
    width: 600,
    height: 280,
    left: 80,
    right: 580,
    top: 20,
    bottom: 220,
  };

  @ViewChild("chartWrap", { static: false })
  chartWrapRef!: ElementRef<HTMLDivElement>;
  tooltipVisible = false;
  tooltipX = 0;
  tooltipY = 0;
  tooltipText = "";
  private refreshSubscription!: Subscription;
  private destroy$ = new Subject<void>();

  constructor(
    private dashboardData: DashboardDataService,
    private route: ActivatedRoute,
    private localStorageService: LocalStorageService,
    private formatHelper: FormatHelperService
  ) { }

  ngOnInit() {
    this.utilityId = this.localStorageService.getUserId();
    console.log(this.utilityId);


    if (!this.utilityId && !this.dashboardData.isPartnerSession()) {
      this.loading = false;
      this.error = "Utility ID not found. Cannot load dashboard data.";
      return;
    }
    const todayISO = DateTime.local().toISODate();
    this.today = todayISO ?? "";
    this.selectedDate =
      this.today;
    this.maxDate = todayISO ?? "";
    this.updateDisplayDate();
    this.loadForSelectedDate();


    this.refreshSubscription = interval(10 * 60 * 1000).subscribe(() => {
      console.log("Refreshing dashboard data on a 10-minute interval...");
      this.loadForSelectedDate();
    });
  }

  get isToday(): boolean {
    return this.selectedDate === this.today;
  }

  private clearStatsToDash() {
    this.stats = null;
    this.loading = false;
    this.error = "";
    this.showDashesForToday = true;
  }

  GetBidType() {
    this.routeData = this.route.snapshot.data;
    this.bid_type = this.routeData?.bid_type ?? BidType.Preferential;
  }


  loadForSelectedDate() {
    this.error = "";
    this.GetBidType();
    this.loading = true;
    this.loadingMessage = "Loading summary for " + this.selectedDate + "…";
    
    this.destroy$.next();

    const startDate = this.selectedDate;
    const endDate = this.selectedDate;

    // If 'daily' (Dynamic), we must also fetch 'monthly' and combine them
    const typesToFetch = this.bid_type === BidType.Daily ? [BidType.Daily, BidType.Monthly] : [this.bid_type || BidType.Preferential];
    
    const requests = typesToFetch.map(type => this.dashboardData.getStatistics(startDate, endDate, type));

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (responses) => {
          console.log('[UtilityDashboard] loadForSelectedDate stats responses:', responses);
          let combinedStats: CombinedStats | null = null;

          responses.forEach(res => {
            const part = res.participation_statistics;
            const summ = res.dashboard_summary;

            if (!combinedStats && part && summ) {
              combinedStats = {
                buyers: part.buyers ?? 0,
                sellers: part.sellers ?? 0,
                active_participants: part.active_participants ?? 0,
                bids_accepted: part.bids_accepted ?? 0,
                bids_rejected: part.bids_rejected ?? 0,
                total_energy_traded: part.total_energy_traded ?? 0,
                total_bids: summ.total_bids ?? 0,
                strike_price: summ.strike_price ?? 0,
                total_value: summ.total_value ?? 0,
                energy_traded: summ.energy_traded ?? 0,
                untraded_energy: summ.untraded_energy ?? 0,
                percentage_change: summ.percentage_change ?? 0,
              };
            } else if (combinedStats && part && summ) {
              combinedStats.buyers += (part.buyers ?? 0);
              combinedStats.sellers += (part.sellers ?? 0);
              combinedStats.active_participants += (part.active_participants ?? 0);
              combinedStats.bids_accepted += (part.bids_accepted ?? 0);
              combinedStats.bids_rejected += (part.bids_rejected ?? 0);
              combinedStats.total_energy_traded += (part.total_energy_traded ?? 0);
              combinedStats.total_bids += (summ.total_bids ?? 0);
              combinedStats.total_value += (summ.total_value ?? 0);
              combinedStats.energy_traded += (summ.energy_traded ?? 0);
              combinedStats.untraded_energy += (summ.untraded_energy ?? 0);
              combinedStats.strike_price = Math.max(combinedStats.strike_price, summ.strike_price ?? 0);
            }
          });

          this.finalizeStats(combinedStats);
        },
        error: (err) => {
          console.error('Statistics API error', err);
          this.finalizeStats(null);
        }
      });
  }

  private finalizeStats(combined: CombinedStats | null) {
    if (combined) {
      this.stats = {
        ...combined,
        average_price: combined.strike_price,
        total_cbdc_transactions: combined.total_value,
        total_traded_value: combined.energy_traded,
        total_number_of_consumers: combined.buyers,
        total_number_of_prosumers: combined.sellers,
        total_number_of_users: combined.active_participants,
      };

      if (this.isToday) {
        const importantValues = [
          this.stats.total_value,
          this.stats.buyers,
          this.stats.sellers,
          this.stats.energy_traded,
          this.stats.strike_price,
          this.stats.active_participants,
          this.stats.total_bids, // Added to ensure bid placement triggers data display
        ];
        // Check if any important value is non-null/undefined and greater than 0
        const anyNonZero = importantValues.some(v => v !== null && v !== undefined && Number(v) > 0);
        this.showDashesForToday = !anyNonZero;
      } else {
        this.showDashesForToday = false;
      }
    } else {
      // If no data was combined (e.g., all requests failed or returned empty)
      this.stats = null;
      this.showDashesForToday = true;
    }
    this.loading = false;
    this.loadChart();
  }

  loadChart() {
    this.chartLoading = true;
    const startDt = DateTime.fromISO(this.selectedDate)
      .minus({ days: 3 })
      .toISODate()!;
    const endDt = DateTime.fromISO(this.selectedDate)
      .plus({ days: 1 })
      .toISODate()!; // exclusive

    const typesToFetch = this.bid_type === BidType.Daily ? [BidType.Daily, BidType.Monthly] : [this.bid_type || BidType.Preferential];
    
    const requests = typesToFetch.map(type => this.dashboardData.getChart(startDt, endDt, type));

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (responses) => {
          console.log('[UtilityDashboard] loadChart responses:', responses);
          const allRows: ChartPoint[] = [];
          responses.forEach(rows => {
            allRows.push(...(rows || []));
          });
          this.finalizeChart(startDt, allRows);
        },
        error: (err) => {
          console.error('Chart API error', err);
          this.chartLoading = false;
        }
      });
  }

  private finalizeChart(startDt: string, allRows: ChartPoint[]) {
    const dates: string[] = [];
    for (let i = 0; i < 4; i++) {
      dates.push(DateTime.fromISO(startDt).plus({ days: i }).toISODate()!);
    }
    
    // Group and aggregate by date
    const map = new Map<string, { buy: number, sell: number, total: number }>();
    dates.forEach(d => map.set(d, { buy: 0, sell: 0, total: 0 }));

    allRows.forEach(r => {
      if (map.has(r.date)) {
        const current = map.get(r.date)!;
        current.buy += (r.buy_volume || 0);
        current.sell += (r.sell_volume || 0);
        current.total += (r.total_volume || 0);
      }
    });

    this.chartGroups = dates.map(d => {
      const it = map.get(d)!;
      return {
        date: d,
        buy_volume: it.buy,
        sell_volume: it.sell,
        total_volume: it.total,
      };
    });

    const rawMax = Math.max(
      ...this.chartGroups.map((g) => g.total_volume),
      1
    );
    this.computeChartTicks(rawMax);
    this.chartLoading = false;
  }

  private computeChartTicks(rawMax: number) {
    const max = Math.max(Math.ceil(rawMax), 1);
    let step = Math.ceil(max / 4);
    const magnitude = Math.pow(10, Math.floor(Math.log10(step)));
    const multipliers = [1, 2, 2.5, 5, 10];
    let chosen = multipliers.find((m) => m * magnitude >= step);
    if (!chosen) chosen = 10;
    step = chosen * magnitude;
    this.chartTicks = [0, step, step * 2, step * 3, step * 4];
    this.chartMaxForScale = step * 4;
  }

  chartHeight(): number {
    return this.SVG.bottom - this.SVG.top;
  }

  groupSpacing(): number {
    const usable = this.SVG.right - this.SVG.left;
    return usable / 4;
  }

  getGroupCenterX(i: number): number {
    const spacing = this.groupSpacing();
    return this.SVG.left + spacing * (i + 0.5);
  }

  getBarWidth(): number {
    return 32;
  }

  getBarX(i: number, offsetIndex: -1 | 0 | 1): number {
    const center = this.getGroupCenterX(i);
    const bw = this.getBarWidth();
    const gap = 5;
    const shift = offsetIndex * (bw + gap);
    return Math.round(center + shift - bw / 2);
  }

  getBarHeightForVolume(v: number): number {
    const maxV = this.chartMaxForScale || 1;
    const h = Math.round((v / maxV) * this.chartHeight());
    return h;
  }

  getBarYForVolume(v: number): number {
    const h = this.getBarHeightForVolume(v);
    return Math.round(this.SVG.bottom - h);
  }

  getYForValue(v: number): number {
    const maxV = this.chartMaxForScale || 1;
    const h = Math.round((v / maxV) * this.chartHeight());
    return Math.round(this.SVG.bottom - h);
  }

  formatXLabel(dateISO: string): string {
    return DateTime.fromISO(dateISO).toFormat("d/M/yy");
  }

  display(value: number | string | undefined | null): string {
    return this.formatHelper.display(value, this.isToday, this.showDashesForToday);
  }

  onBarHover(event: MouseEvent, value: number) {
    this.tooltipText = value == null ? "—" : String(value);

    const wrapEl = this.chartWrapRef?.nativeElement;
    if (!wrapEl) {
      this.tooltipX = event.clientX + 8;
      this.tooltipY = event.clientY - 28;
    } else {
      const rect = wrapEl.getBoundingClientRect();
      const localX = event.clientX - rect.left;
      const localY = event.clientY - rect.top;
      this.tooltipX = Math.round(localX + 8);
      this.tooltipY = Math.round(localY - 28);
    }
    this.tooltipVisible = true;
  }

  hideTooltip() {
    this.tooltipVisible = false;
  }

  prevDay() {
    const dt = DateTime.fromISO(this.selectedDate).plus({ days: -1 });
    this.selectedDate = dt.toISODate() ?? this.selectedDate;
    this.updateDisplayDate();
    this.loadForSelectedDate();
  }

  nextDay() {
    const dt = DateTime.fromISO(this.selectedDate).plus({ days: 1 });
    if (dt > DateTime.fromISO(this.today)) {
      this.selectedDate = this.today;
    } else {
      this.selectedDate = dt.toISODate() ?? this.selectedDate;
    }
    this.updateDisplayDate();
    this.loadForSelectedDate();
  }

  onDateChange() {
    const sel = DateTime.fromISO(this.selectedDate);
    const tod = DateTime.fromISO(this.today);
    
    if (sel > tod) {
      this.selectedDate = this.today;
    }
    
    this.loadForSelectedDate();
  }

  onMatDateChange(event: any) {
    if (event.value) {
      let dt = DateTime.fromJSDate(event.value);
      const todayDt = DateTime.fromISO(this.today);
      if (dt > todayDt) dt = todayDt;

      if (dt.isValid) {
        this.selectedDate = dt.toISODate() ?? this.today;
        this.updateDisplayDate();
        this.loadForSelectedDate();
      }
    }
  }

  private updateDisplayDate() {
    this.displayDate = DateTime.fromISO(this.selectedDate).toFormat("d MMMM yyyy");
  }

  handleError(err: any) {
    this.error =
      err?.error?.message ||
      err?.error?.detail ||
      err.message ||
      "Failed to load dashboard data";

    if (this.isToday) {
      this.showDashesForToday = true;
    }
  }

  public abs(value: number | undefined | null): number {
    if (value === undefined || value === null) {
      return 0;
    }
    return Math.abs(value);
  }

  ngOnDestroy(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
